/**
 * Settings › Accounting › Card Surcharge (piece 24; decisions 0239, 1620): the
 * percentage a credit card payment adds for the customer, beneath the amount
 * where tax sits on an invoice.
 *
 * 🔨 The card networks cap a surcharge at three percent and forbid one on a
 * debit or prepaid card. The credit rate is the one Settings row; debit and
 * prepaid stand at zero in code, shown read-only on the screen, because a rate
 * the networks' rules hold at zero is not a setting (decision 1620).
 */

/** The one surcharge row: a credit card's percentage, seeded at 3, and 0 turns the line off. */
export const CARD_SURCHARGE_PERCENT_KEY = 'payments.cardSurchargePercent';
export const CARD_SURCHARGE_PERCENT_DEFAULT = 3;
/** The networks' cap; the editor refuses anything above it. */
export const CARD_SURCHARGE_CAP_PERCENT = 3;

/** The card types the Checkout asks for before the card (decision 0372). */
export const CARD_TYPES = ['Credit', 'Debit', 'Prepaid'] as const;
export type CardType = (typeof CARD_TYPES)[number];

export interface CardSurchargeRate {
  cardType: CardType;
  percent: number;
  /** False for debit and prepaid, which the networks forbid a surcharge on. */
  editable: boolean;
}

/** What `GET /banking/settings/card-surcharge` answers: one rate per card type, and whether this viewer may change the credit rate. */
export interface CardSurchargeAnswer {
  rates: CardSurchargeRate[];
  capPercent: number;
  /** A super admin's alone (decision 1620). */
  canWrite: boolean;
}

/** The refusal a typed percentage earns, or null: a number from 0 to the cap, at most two decimal places. */
export function cardSurchargeProblem(value: unknown): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'The card surcharge is a percentage, such as 3 or 2.5.';
  if (value < 0) return 'The card surcharge cannot be below 0; 0 turns the line off.';
  if (value > CARD_SURCHARGE_CAP_PERCENT) return `The card networks cap a surcharge at ${CARD_SURCHARGE_CAP_PERCENT} percent, so the card surcharge cannot be above ${CARD_SURCHARGE_CAP_PERCENT}.`;
  // Compared with a tolerance: 1.15 * 100 is 114.99999999999999 in floating point, and an exact test refused it.
  if (Math.abs(value * 100 - Math.round(value * 100)) >= 1e-9) return 'The card surcharge carries at most two decimal places.';
  return null;
}

/** The rate each card type carries, given the stored credit rate; an absent row reads as the default. */
export function cardSurchargeRates(creditPercent: unknown): CardSurchargeRate[] {
  const credit = typeof creditPercent === 'number' && Number.isFinite(creditPercent) ? creditPercent : CARD_SURCHARGE_PERCENT_DEFAULT;
  return CARD_TYPES.map((cardType) => (cardType === 'Credit' ? { cardType, percent: credit, editable: true } : { cardType, percent: 0, editable: false }));
}
